import React from "react";
import { defaultColors, styled } from "../../stitches.config";
import { body } from "../commonStyles/Fonts";

import BrandsConfig, { TitleBrandsVariants } from "../../config";

interface Field {
  name: string;
  link: string;
}

interface CategoryObject {
  name: string;
  path: string;
  parents?: Array<{
    name: string;
    path: string;
  }>;
}

interface Props {
  category: Field;
  subCategory?: Field;
  categoryObject?: CategoryObject;
  invert?: boolean;
  allWhite?: boolean;
}

const Breadcrumbs: React.FC<Props> = ({
  category,
  subCategory,
  categoryObject,
  invert,
  allWhite,
}) => {
  const parentCategory = categoryObject?.parents?.[0];
  const hasParentFromObject = !!parentCategory;
  const getLogoType = () => {
    if (invert) return "invert";
    if (allWhite) return "light";
  };
  const getLinkNameType = () => {
    if (allWhite) return "light";
    if (subCategory || hasParentFromObject) {
      return invert ? "invert" : "secondary";
    }
    return "main";
  };

  const ArrowIcon = () => (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.6788 0.446411L2.11311 1.0121L5.10098 3.99996L2.11311 6.98783L2.6788 7.55352L6.23235 3.99996L2.6788 0.446411Z"
      />
    </svg>
  );

  return (
    <Wrapper className="breadcrumbs">
      <ListWrapper
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        <li
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <Logo
            href='/'
            className={body()}
            type={getLogoType()}
            itemProp="item"
          >
            <BrandTitle brand={window.BRAND} itemProp="name">
              {BrandsConfig[window.BRAND].brandTitle}
            </BrandTitle>
          </Logo>

          <meta itemProp="position" content="1" />
        </li>

        {parentCategory && (
          <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <Arrow className="primary-svg">
              <ArrowIcon />
            </Arrow>

            <LinkName
              href={parentCategory.path}
              className={body()}
              type={getLinkNameType()}
              itemProp="item"
            >
              <span itemProp="name">{parentCategory.name}</span>
            </LinkName>

            <meta itemProp="position" content="2" />
          </li>
        )}

        <li
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <Arrow
            className={
              subCategory || hasParentFromObject
                ? "primary-svg"
                : "gray-svg"
            }
          >
            <ArrowIcon />
          </Arrow>

          <LinkName
            href={category.link}
            className={body()}
            type={getLinkNameType()}
            itemProp="item"
          >
            <span itemProp="name">{category.name}</span>
          </LinkName>

          <meta
            itemProp="position"
            content={hasParentFromObject ? "3" : "2"}
          />
        </li>

        {subCategory && !hasParentFromObject && (
          <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <Arrow className="gray-svg">
              <ArrowIcon />
            </Arrow>

            <LinkName
              href={subCategory.link || window.location.pathname}
              className={body()}
              type="main"
              itemProp="item"
            >
              <span itemProp="name">{subCategory.name}</span>
            </LinkName>

            <meta itemProp="position" content="3" />
          </li>
        )}
      </ListWrapper>
    </Wrapper>
  );
};

const ListWrapper = styled("ul", {
  display: "flex !important",
  alignItems: "center",
});

const Wrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  img: {
    margin: "0 8px",
    width: "8px",
    height: "8px",
  },
  ul: {
    display: "inline-block",
    paddingLeft: 0,
  },
  li: {
    display: "flex",
    alignItems: "center",
  },
  fontWeight: "600 !important",
  ".primary-svg": {
    svg: {
      fill: "$primary",
    },
  },
  ".gray-svg": {
    svg: {
      fill: "#8D8D8D",
    },
  },
});

const Arrow = styled("div", {
  margin: "0 8px",
});

const Logo = styled("a", {
  color: "$grayscale0",
  variants: {
    type: {
      invert: {
        color: "$grayscale100",
      },
      light: {
        color: defaultColors.grayscale0,
      },
    },
  },
});

const BrandTitle = styled("span", {
  variants: {
    brand: TitleBrandsVariants.breadcrumbsColorVariants
  },
});

const LinkName = styled("a", {
  variants: {
    type: {
      main: {
        color: `${defaultColors.grayscale75} !important`,
      },
      secondary: {
        color: "$grayscale0",
      },
      invert: {
        color: "$grayscale100 !important",
      },
      light: {
        color: defaultColors.grayscale0,
      },
    },
  },
});

export default Breadcrumbs;
